import { slugRef } from './refs.js';
import type { Entity, FieldDef, NodeKind, NodeType, Story, StoryNode, TextDocument, TimelineData, TypeCategory } from './types.js';

export type TypeTemplate = Omit<NodeType, 'id' | 'textual' | 'placeholderId'> & { key: string; textual?: boolean };

const text = (key: string, label: string): FieldDef => ({ key, label, type: 'text' });
const long = (key: string, label: string): FieldDef => ({ key, label, type: 'longtext' });
const num = (key: string, label: string): FieldDef => ({ key, label, type: 'number' });
const image = (key: string, label: string): FieldDef => ({ key, label, type: 'image' });

/** Das Feld der Geschichte (Wurzel): Prämisse / Logline / Hook, mit Untertitel und Beispiel. */
export const STORY_PREMISE = {
  label: 'Prämisse / Logline / Hook',
  help: 'Inhalt: Bringt den Kernkonflikt der Geschichte auf den Punkt. Nennt meist die Hauptfigur, das auslösende Ereignis und das zentrale Problem oder Ziel. Das Ende wird hier noch nicht verraten.',
  placeholder: 'Beispiel: „Ein unzufriedener Büroangestellter gründet einen geheimen Boxklub, der sich zu einer landesweiten anarchistischen Organisation entwickelt.“ (Fight Club)',
} as const;

/** Frühere Namen vordefinierter Klassen → heutiger Name (Ordner älterer Projekte, Umbenennung beim Öffnen). */
export const LEGACY_BUILTIN_NAMES: Readonly<Record<string, string>> = { Zeitstrahl: 'Zeitlinie' };

/** Titel der ersten Zeitlinie einer neuen Geschichte (früher „Hauptzeitstrahl“) */
export const MAIN_TIMELINE_TITLE = 'Hauptzeitlinie';

/**
 * Frühere Standardnamen einer älteren Geschichte auf den heutigen Stand: vordefinierte Klassen (`LEGACY_BUILTIN_NAMES`, sofern der neue
 * Name frei ist) und die unveränderten Titel der Zeitlinien („Hauptzeitstrahl“, „Zeitstrahl 2“). Selbst vergebene Namen bleiben.
 */
export function legacyNameFixes(story: Story): { types: { id: string; name: string }[]; nodes: { id: string; fields: StoryNode['fields'] }[] } {
  const taken = new Set(story.types.map((t) => t.name));
  const types = story.types.flatMap((t) => {
    const now = t.builtin ? LEGACY_BUILTIN_NAMES[t.name] : undefined;
    return now && !taken.has(now) ? [{ id: t.id, name: now }] : [];
  });
  const nodes = story.nodes.flatMap((n) => {
    const old = n.fields.title;
    if (n.kind !== 'timeline' || typeof old !== 'string') return [];
    const title = old === 'Hauptzeitstrahl' ? MAIN_TIMELINE_TITLE : old.replace(/^Zeitstrahl (\d+)$/, 'Zeitlinie $1');
    return title === old ? [] : [{ id: n.id, fields: { ...n.fields, title } }];
  });
  return { types, nodes };
}

/** Vordefinierte Typen jedes Projekts. Eigene Typen kommen vom Nutzer (Kategorie Objekt, Ort oder Normal). */
export const BUILTIN_TYPES: TypeTemplate[] = [
  {
    key: 'story',
    name: 'Geschichte',
    category: 'story',
    builtin: true,
    color: '#2b2b2b',
    fields: [
      text('title', 'Titel'),
      // Schlüssel bleibt `summary` (bestehende Werte), die Beschriftung ist die Prämisse
      { ...long('summary', STORY_PREMISE.label), help: STORY_PREMISE.help, placeholder: STORY_PREMISE.placeholder },
    ],
    titleKey: 'title',
    hoverKey: 'summary',
    imageKey: null,
    textual: true,
    sort: 0,
  },
  {
    key: 'premise',
    name: 'Prämisse',
    category: 'foundational',
    builtin: true,
    color: '#7a5c8e',
    fields: [long('message', 'Kernaussage der Geschichte'), text('theme', 'Thema'), long('question', 'Zentrale Frage'), long('stakes', 'Einsatz und Konsequenzen')],
    titleKey: 'theme',
    hoverKey: 'message',
    imageKey: null,
    sort: 1,
  },
  {
    key: 'world',
    name: 'Welt',
    category: 'foundational',
    builtin: true,
    color: '#4a8796',
    fields: [long('description', 'Beschreibung der Welt'), text('era', 'Epoche / Zeit'), long('rules', 'Regeln und Gesetzmäßigkeiten'), long('atmosphere', 'Atmosphäre und Stil'), image('image', 'Bild')],
    titleKey: 'era',
    hoverKey: 'description',
    imageKey: 'image',
    sort: 2,
  },
  { key: 'timeline', name: 'Zeitlinie', category: 'timeline', builtin: true, color: '#857c72', fields: [text('title', 'Titel')], titleKey: 'title', hoverKey: null, imageKey: null, sort: 3 },
  {
    key: 'scene',
    name: 'Szene',
    category: 'scene',
    builtin: true,
    color: '#3f7f78',
    // die Szene ist das einzige Objekt der Handlung: als Zeitpunkt (Ereignis) oder Zeitraum auf der Fläche, im Manuskript in einem Kapitel
    fields: [
      text('title', 'Titel'),
      long('description', 'Kurzbeschreibung'),
      { key: 'pov', label: 'Perspektive', type: 'ref', refType: 'Person', help: 'Aus wessen Sicht wird erzählt?' },
      // der erste im Draft verknüpfte Ort wird es von selbst (PLAN.md §16a)
      { key: 'place', label: 'Schauplatz', type: 'ref', refType: 'Ort', help: 'Wo spielt die Szene? Ohne eigene Wahl: der erste Ort, den der Szenentext verknüpft.' },
      { ...long('goal', 'Ziel'), help: 'Was will die Perspektivfigur in dieser Szene erreichen?' },
      { ...long('conflict', 'Konflikt'), help: 'Was stellt sich ihr in den Weg?' },
      { ...long('outcome', 'Ausgang'), help: 'Wie endet die Szene – erreicht, gescheitert, mit neuer Wendung?' },
      { ...text('status', 'Status'), options: ['Idee', 'Entwurf', 'überarbeitet', 'fertig'] },
      image('image', 'Szenenbild'),
      // die Zeit ist kein Feld, sondern eine eigene Angabe der Szene (`time`, PLAN.md §15)
    ],
    titleKey: 'title',
    hoverKey: 'description',
    imageKey: 'image',
    textual: true,
    sort: 4,
  },
  {
    key: 'person',
    name: 'Person',
    category: 'object',
    builtin: true,
    color: '#4f6d8f',
    // nur das Nötigste; weitere Felder fügt man hinzu (Vorschläge im Design-Modus), sie gelten dann für alle Personen des Projekts
    fields: [text('name', 'Name'), long('description', 'Kurzbeschreibung'), image('photo', 'Bild')],
    titleKey: 'name',
    hoverKey: 'description',
    imageKey: 'photo',
    sort: 6,
  },
  {
    key: 'place',
    name: 'Ort',
    category: 'place',
    builtin: true,
    color: '#6f8f5b',
    fields: [text('name', 'Name'), long('description', 'Kurzbeschreibung'), image('image', 'Bild')],
    titleKey: 'name',
    hoverKey: 'description',
    imageKey: 'image',
    sort: 7,
  },
  {
    key: 'landscape',
    name: 'Landschaft',
    category: 'place',
    builtin: true,
    color: '#5f8a4e',
    fields: [text('name', 'Name'), long('description', 'Kurzbeschreibung'), image('image', 'Bild')],
    titleKey: 'name',
    hoverKey: 'description',
    imageKey: 'image',
    sort: 14,
  },
  {
    key: 'organization',
    name: 'Organisation',
    category: 'object',
    builtin: true,
    color: '#b5563a',
    fields: [text('name', 'Name'), long('description', 'Kurzbeschreibung'), image('image', 'Logo / Bild')],
    titleKey: 'name',
    hoverKey: 'description',
    imageKey: 'image',
    sort: 8,
  },
  {
    key: 'object',
    name: 'Gegenstand',
    category: 'object',
    builtin: true,
    color: '#a8506c',
    fields: [text('name', 'Name'), long('description', 'Kurzbeschreibung'), image('image', 'Bild')],
    titleKey: 'name',
    hoverKey: 'description',
    imageKey: 'image',
    sort: 9,
  },
  {
    key: 'concept',
    name: 'Konzept',
    category: 'object',
    builtin: true,
    color: '#8c8a3e',
    fields: [text('name', 'Name'), long('description', 'Kurzbeschreibung'), image('image', 'Bild')],
    titleKey: 'name',
    hoverKey: 'description',
    imageKey: 'image',
    sort: 10,
  },
  {
    key: 'metadata',
    name: 'Metadaten',
    category: 'normal',
    builtin: true,
    color: '#c08a2e',
    // Sammelstelle für zusätzliche Informationen (dazu kommen frei benannte zusätzliche Felder an jedem Node)
    fields: [text('title', 'Titel'), long('notes', 'Notizen')],
    titleKey: 'title',
    hoverKey: 'notes',
    imageKey: null,
    sort: 11,
  },
  // Gliederung des Manuskripts: wie und in welcher Reihenfolge erzählt wird (nie auf der Planungsfläche)
  {
    key: 'manuscript',
    name: 'Manuskript',
    category: 'manuscript',
    builtin: true,
    color: '#3d5f5b',
    fields: [
      text('title', 'Titel'),
      { ...text('voice', 'Erzählform'), options: ['Ich', 'Er/Sie', 'Du', 'auktorial'] },
      { ...text('tense', 'Zeitform'), options: ['Präteritum', 'Präsens'] },
      {
        ...num('targetWords', 'Ziel-Wortzahl'),
        help: 'Umfang des ganzen Buchs in Wörtern. Die Story Beats rechnen damit ihre Stellen in Wörtern; ist das Buch noch nicht ganz geplant, liegen die Szenen im vorderen Teil.',
      },
    ],
    titleKey: 'title',
    hoverKey: null,
    imageKey: null,
    sort: 12,
  },
  // Abschnitt: fasst Kapitel zusammen (ein Teil des Buchs, ein Akt); optional – Kapitel können auch direkt im Manuskript stehen
  {
    key: 'section',
    name: 'Abschnitt',
    category: 'section',
    builtin: true,
    color: '#45625f',
    fields: [text('title', 'Titel'), long('description', 'Kurzbeschreibung')],
    titleKey: 'title',
    hoverKey: 'description',
    imageKey: null,
    sort: 13,
  },
  {
    key: 'chapter',
    name: 'Kapitel',
    category: 'chapter',
    builtin: true,
    color: '#4e7470',
    fields: [
      text('title', 'Titel'),
      long('description', 'Kurzbeschreibung'),
      num('targetWords', 'Ziel-Wortzahl'),
    ],
    titleKey: 'title',
    hoverKey: 'description',
    imageKey: null,
    sort: 13,
  },
  // Notizzettel auf der Handlungsfläche: ein Text in einer Farbe (`tagColor`), frei in Lage und Größe (`w`, `h`); lässt sich in ein
  // Ereignis umwandeln (A141)
  {
    key: 'note',
    name: 'Notiz',
    category: 'normal',
    builtin: true,
    color: '#f6e27a',
    fields: [long('text', 'Text')],
    titleKey: 'text',
    hoverKey: null,
    imageKey: null,
    sort: 15,
  },
];

/** Vordefinierte Klasse der Notizzettel */
export const isNoteType = (t: Pick<NodeType, 'name' | 'builtin'> | undefined): boolean => !!t && t.builtin && t.name === 'Notiz';
/** Standardgröße eines Notizzettels und die kleinste */
export const NOTE_SIZE = { w: 220, h: 160, minW: 120, minH: 80 } as const;

/** Kategorien der Erzählebene (Manuskript, Abschnitt, Kapitel, Szene) */
export const NARRATIVE_CATEGORIES: readonly TypeCategory[] = ['manuscript', 'section', 'chapter', 'scene'];
/** Gliederung des Manuskripts (Manuskript, Abschnitt, Kapitel): nie auf der Fläche. Szenen gehören zu beidem (siehe `scene.ts`). */
export const isNarrativeKind = (kind: NodeKind): boolean => kind === 'manuscript' || kind === 'section' || kind === 'chapter' || kind === 'scene';
/** Gliederung des Manuskripts ohne die Szenen: Abschnitt und Kapitel (haben eine Stelle über `rank`, lassen sich nicht kopieren) */
export const isOutlineKind = (kind: NodeKind): boolean => kind === 'section' || kind === 'chapter';

/** Verweisschlüssel der Foundationals */
const FOUNDATIONAL_KEYS: Record<string, string> = { Prämisse: 'praemisse', Welt: 'welt' };

/** Standarddokument eigener Klassen mit Texten (Häkchen „Textdokumente“). Szenen haben den Draft, die Geschichte Pitch und Exposé. */
export const DEFAULT_DOCUMENT_NAMES = ['Zusammenfassung'] as const;

export const newDocuments = (newId: () => string): TextDocument[] => DEFAULT_DOCUMENT_NAMES.map((name) => ({ id: newId(), name, text: '', locked: true }));

/** Standarddokument einer Szene: der Manuskripttext. */
export const DRAFT = 'Draft';
export const SCENE_DOCUMENT_NAMES = [DRAFT] as const;

export const newSceneDocuments = (newId: () => string, text = ''): TextDocument[] => SCENE_DOCUMENT_NAMES.map((name) => ({ id: newId(), name, text, locked: true }));

/** Standarddokumente der Geschichte (Wurzel): der Pitch und das Exposé. */
export const STORY_DOCUMENT_NAMES = ['Der Pitch', 'Das Exposé'] as const;

export const newStoryDocuments = (newId: () => string): TextDocument[] => STORY_DOCUMENT_NAMES.map((name) => ({ id: newId(), name, text: '', locked: true }));

/** Namen der Standarddokumente eines Nodes (Geschichte: Pitch und Exposé, Szene: Draft, eigene Klassen: Zusammenfassung). */
export const defaultDocumentNames = (kind: NodeKind): readonly string[] =>
  kind === 'story' ? STORY_DOCUMENT_NAMES : kind === 'event' || kind === 'period' || kind === 'scene' ? SCENE_DOCUMENT_NAMES : DEFAULT_DOCUMENT_NAMES;

/** Erklärung, was in ein Standarddokument gehört (steht im Texteditor über dem Text). Nach Name des Dokuments. */
export const DOCUMENT_HINTS: Record<string, string> = {
  'Der Pitch':
    'Inhalt: Eine knackige, emotionale Zusammenfassung, die Neugier wecken soll. Sie kombiniert die Prämisse oft mit dem Alleinstellungsmerkmal (USP) des Buchs oder vergleicht es mit bekannten Werken (z. B. „Harry Potter trifft auf Die Tribute von Panem“).',
  'Das Exposé':
    'Inhalt: Das wichtigste Arbeits- und Verkaufsdokument. Es enthält die vollständige Zusammenfassung der Handlung (inklusive des Endes!), kurze Steckbriefe der Hauptfiguren, Informationen zu Genre, Zielgruppe und Umfang sowie die thematische Kernbotschaft.',
};

export const DEFAULT_TIMELINE: TimelineData = { startPos: 0, endPos: 1600, startLabel: '', endLabel: '', flags: [] };

export interface ProjectSeed {
  types: NodeType[];
  entities: Entity[];
  nodes: StoryNode[];
  rootId: string;
  timelineId: string;
  manuscriptId: string;
}

/** Sortierschlüssel des ersten Kapitels bzw. der ersten Szene (Mitte des Schlüsselraums, siehe `rankBetween`). */
export const FIRST_RANK = 'i';

/**
 * Grundausstattung eines neuen Projekts: vordefinierte Typen, die Foundationals Prämisse und Welt (je genau einmal),
 * die Story als Wurzel, ein bereits angelegter Zeitstrahl und das Manuskript mit einem ersten Kapitel.
 */
export function projectSeed(newId: () => string, storyTitle = 'Meine Geschichte', now = new Date().toISOString()): ProjectSeed {
  const types: NodeType[] = BUILTIN_TYPES.map(({ key: _key, textual, ...t }) => ({ ...t, textual: textual ?? false, placeholderId: null, id: newId() }));
  const byName = (name: string): NodeType => types.find((t) => t.name === name)!;
  const entities: Entity[] = types.filter((t) => t.category === 'foundational').map((t) => ({ id: newId(), typeId: t.id, fields: {}, refKey: FOUNDATIONAL_KEYS[t.name] ?? slugRef(t.name), x: null, y: null }));
  const rootId = newId();
  const timelineId = newId();
  const manuscriptId = newId();
  const base = { entityId: null, dynamicFields: [], documents: [] as TextDocument[], tagColor: null, x: null, y: null, at: null, from: null, to: null, timeline: null, createdAt: now };
  const nodes: StoryNode[] = [
    { ...base, id: rootId, parentId: null, typeId: byName('Geschichte').id, kind: 'story', fields: { title: storyTitle }, documents: newStoryDocuments(newId) },
    {
      ...base,
      id: timelineId,
      parentId: rootId,
      typeId: byName('Zeitlinie').id,
      kind: 'timeline',
      fields: { title: MAIN_TIMELINE_TITLE },
      x: 0,
      y: 0,
      timeline: { ...DEFAULT_TIMELINE, flags: [] },
    },
    { ...base, id: manuscriptId, parentId: rootId, typeId: byName('Manuskript').id, kind: 'manuscript', fields: { title: storyTitle }, rank: null, narrates: [], narrateSkip: false },
    { ...base, id: newId(), parentId: manuscriptId, typeId: byName('Kapitel').id, kind: 'chapter', fields: { title: 'Kapitel 1' }, rank: FIRST_RANK, narrates: [], narrateSkip: false, colorSlot: 0 },
  ];
  return { types, entities, nodes, rootId, timelineId, manuscriptId };
}
